// The Herself campaign board, saving the decisions as they are taken.
//
// Same shape as auston_board_controller, and deliberately its own file rather
// than a shared one: the two boards answer different questions (a hemline and
// a shoe there, a garment family here), and the last thing a board like this
// needs is a generic controller that both brands have to keep agreeing with.
//
// The board is still one big form — a campaign is SET UP in one pass, and the
// casting, the backdrop, the views and every row's colour label and note
// belong to that one save. But a status and a garment family are answers an
// operator gives while looking at a row, and making them wait for "Save board"
// is how a board drifts from what the person reading it already knows.
//
// Markup:
//   <div data-controller="herself-board"
//        data-herself-board-url-value="<%= inline_update_path %>">
//     <select data-action="change->herself-board#save"
//             data-hs-field="status" data-hs-look="12">
//     <select data-action="change->herself-board#save"
//             data-hs-field="family" data-hs-group="adulta::maio básico">
//     <span data-hs-flag></span>   <!-- anywhere inside the control's parent -->
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, status: String, band: String }

  // What the server last confirmed for each self-saving control. A failed save
  // has to put the control BACK: a select still showing "Delivered" over a row
  // the database never moved is worse than no auto-save at all.
  connect() {
    this.element.querySelectorAll("[data-hs-field]").forEach((el) => {
      el.dataset.hsSaved = el.value
    })
  }

  save(event) {
    const select = event.target
    const value = select.value
    // The "mixed" placeholder is a description, not an answer.
    if (!value) return

    this.flag(select, "saving")

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.token()
      },
      body: JSON.stringify({
        field: select.dataset.hsField,
        value: value,
        look_id: select.dataset.hsLook,
        look: select.dataset.hsGroup,
        band: this.bandValue
      })
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        select.dataset.hsSaved = value
        this.flag(select, "saved")
        if (select.dataset.hsField === "status") this.applyFilter(select, data.value)
        this.applyReadiness(data.looks)
        this.applyTally(data.tally)
      })
      .catch((r) => this.failed(select, r))
  }

  // Say what went wrong, and undo the control. The status code goes in the
  // tooltip so a server error can be told from a lost connection without
  // opening the console.
  failed(select, response) {
    const code = response && response.status ? response.status : "no reply"
    if (select.dataset.hsSaved !== undefined) select.value = select.dataset.hsSaved

    this.flag(select, "error")
    const mark = select.parentElement?.querySelector("[data-hs-flag]")
    if (mark) mark.title = `Not saved (${code}) — the row was put back.`

    if (response && typeof response.json === "function") {
      response.json().then((body) => {
        if (mark && body && body.error) mark.title = `Not saved (${code}): ${body.error}`
      }).catch(() => {})
    }
  }

  // Both fields can clear or create a blocker, so the tick box follows the
  // server's verdict rather than a guess made here.
  applyReadiness(looks) {
    (looks || []).forEach(({ id, ready, blockers }) => {
      const box = this.element.querySelector(`input[name="look_ids[]"][value="${id}"]`)
      if (box) {
        box.disabled = !ready
        if (!ready) box.checked = false
        box.title = ready ? "" : `blocked: ${(blockers || []).join(", ")}`
      }

      const warn = this.element.querySelector(`[data-hs-blockers="${id}"]`)
      if (warn) {
        warn.textContent = ready ? "" : `blocked: ${(blockers || []).join(", ")}`
        warn.hidden = ready
      }
    })
  }

  // The counts on the status chips are only true until someone changes a
  // status — which is the one thing this board is for. The server sends the
  // recount back with every save.
  applyTally(tally) {
    if (!tally) return

    this.element.querySelectorAll("[data-hs-tally]").forEach((mark) => {
      mark.textContent = tally[mark.dataset.hsTally] || 0
    })
  }

  // Filtered to one stage, a row answered out of that stage is no longer part
  // of the set. It stays put — removing it would move everything under the
  // cursor — and dims instead, so what is left reads as the filter.
  applyFilter(select, value) {
    if (!this.hasStatusValue || !this.statusValue) return

    select.closest(".hs-row")?.classList.toggle("hs-row--out", value !== this.statusValue)
  }

  flag(select, state) {
    const mark = select.parentElement?.querySelector("[data-hs-flag]")
    if (!mark) return

    const marks = { saving: "…", saved: "✓", error: "✕ not saved" }
    mark.textContent = marks[state] || ""
    if (state !== "error") mark.title = ""
    mark.style.color = state === "error" ? "#b42318" : state === "saved" ? "#2ea067" : "#9aa0a6"
    if (state === "saved") {
      setTimeout(() => {
        if (mark.textContent === "✓") mark.textContent = ""
      }, 1500)
    }
  }

  token() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
